import type { InfluencerData } from "@/features/influencer-monitoring/store/influencerStore";
import { PLATFORM_KEYS } from "@/constants/platformData";
import { extractPlatformAndUsernameFromUrl } from "./urlUtils";

/**
 * Normalize link for duplicate comparison (trim + lowercase).
 */
export function normalizeLink(url: string): string {
  return url.trim().toLowerCase();
}

/**
 * Returns true if the URL is a valid profile URL for a supported platform
 * (Facebook, X/Twitter, Instagram, Reddit, YouTube, TikTok).
 * Invalid examples: linkedin.com/feed, facebook.com (no path), etc.
 */
export function isValidProfileUrl(url: string): boolean {
  if (!url || typeof url !== "string" || url.trim() === "") return false;
  return extractPlatformAndUsernameFromUrl(url.trim()) !== null;
}

export interface LinkValidationResult {
  invalidLinks: Set<string>;
  duplicateLinks: Set<string>;
}

/**
 * Collects all non-empty links from influencer data and returns
 * - invalidLinks: trimmed links that are not valid profile URLs
 * - duplicateLinks: normalized links that appear more than once
 */
export function getLinkValidation(
  data: InfluencerData[],
): LinkValidationResult {
  const invalidLinks = new Set<string>();
  const countByNormalized = new Map<string, number>();

  for (const row of data ?? []) {
    for (const key of PLATFORM_KEYS) {
      const raw = row[key];
      if (typeof raw !== "string") continue;
      const trimmed = raw.trim();
      if (trimmed === "") continue;

      if (!isValidProfileUrl(trimmed)) {
        invalidLinks.add(trimmed);
      }

      const norm = normalizeLink(trimmed);
      countByNormalized.set(norm, (countByNormalized.get(norm) ?? 0) + 1);
    }
  }

  const duplicateLinks = new Set<string>();
  countByNormalized.forEach((count, norm) => {
    if (count > 1) duplicateLinks.add(norm);
  });

  return { invalidLinks, duplicateLinks };
}
